import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingNavbar from './Navbar';
import LandingFooter from './LandingFooter';
import './landing-animations.css';
import './landing-theme.css';
import './landing-eb.css';
import './legal.css'; // last, so the legal styles win

const formatDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

/**
 * Shared layout for the Privacy policy and Terms of use pages.
 *
 * Props
 *  - doc:   { isSample, eyebrow, title, summary, lastUpdated, version, highlights[], sections[], contact }
 *           (see legalContent.js for the shape)
 *  - other: { to, label } link to the sibling legal page
 */
const LegalPage = ({ doc, other }) => {
  const sections = useMemo(() => doc?.sections || [], [doc]);
  const [activeId, setActiveId] = useState(sections[0]?.id);

  // Highlight the table of contents entry for the section being read.
  useEffect(() => {
    if (!sections.length || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-20% 0px -65% 0px' }
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  useEffect(() => {
    if (doc?.title) document.title = `${doc.title} | Ethosboard`;
  }, [doc]);

  if (!doc) return null;
  const { contact } = doc;

  return (
    <>
    <LandingNavbar />
    <main className="legal">
      <header className="legal__hero">
        <div className="legal__hero-text">
          <p className="legal__eyebrow">{doc.eyebrow}</p>
          <h1>{doc.title}</h1>
          <p className="legal__summary">{doc.summary}</p>
          <p className="legal__meta">
            Last updated {formatDate(doc.lastUpdated)}
            {doc.version ? `, version ${doc.version}` : ''}
          </p>
          {doc.isSample && (
            <p className="legal__sample" role="note">
              Sample content. The final text will be added once it is approved.
            </p>
          )}
        </div>

        {doc.highlights?.length > 0 && (
          <aside className="legal__glance" aria-label="In short">
            <h2>In short</h2>
            <ul>
              {doc.highlights.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          </aside>
        )}
      </header>

      <div className="legal__layout">
        <nav className="legal__toc" aria-label={`${doc.title} contents`}>
          <p className="legal__toc-title">On this page</p>
          <ol>
            {sections.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className={s.id === activeId ? 'is-active' : undefined}
                  aria-current={s.id === activeId ? 'true' : undefined}
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ol>
          <button type="button" className="legal__print" onClick={() => window.print()}>
            Print this page
          </button>
        </nav>

        <article className="legal__article">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="legal__section">
              <h2>
                <span className="legal__num">{i + 1}.</span> {s.title}
              </h2>
              {s.body.map((block, j) =>
                Array.isArray(block) ? (
                  <ul key={j}>
                    {block.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p key={j}>{block}</p>
                )
              )}
            </section>
          ))}
        </article>
      </div>

      {contact && (
        <section className="legal__contact">
          <div>
            <h2>{contact.heading}</h2>
            <p>{contact.text}</p>
          </div>
          <div className="legal__contact-actions">
            {contact.email && (
              <a className="legal__btn legal__btn--gold" href={`mailto:${contact.email}`}>
                Email {contact.email}
              </a>
            )}
            {other && (
              <Link className="legal__btn legal__btn--ghost" to={other.to}>
                Read the {other.label}
              </Link>
            )}
          </div>
        </section>
      )}
    </main>
    <LandingFooter />
    </>
  );
};

export default LegalPage;
